import React, { useState, useEffect, useCallback } from 'react';
import { Edit, XCircle, ChevronDown, ChevronUp } from 'lucide-react'; // For the edit and close icons
import { QUESTIONDIFFICULTY, QUIZTYPE, TAGLEVELS, defaultPageSize, defaultTotalCount, QUIZ_TYPE_LABELS, MICRO_MAX_QUESTIONS, DIVISION_VALUES, UNIT_VALUES, isDivisionLevel, isUnitLevel, ALL_SUBJECTS_OPTION } from '../utils/Constants';
import { isEmpty } from '../utils/BasicUtilities';
import { getAdminQuestionUrl, getAdminQuizDeleteAndPostUrl, getAdminQuizGetByIdUrl, getAdminQuizGetUrl, getAdminQuizPutUrl, getTagUrl, getAdminQuestionGetByIdUrl } from '../utils/UrlUtilities';
import { constructQuizPostPayload, getAdminQuizDeletePayload } from '../utils/PayloadUtils';
import { useApiCaller } from '../utils/ApiUtilities';
import { useUser } from '../contexts/UserContext';
import { QuestionFilterBar, QuestionSelectionGrid, SubjectQuestionGrid } from './QuizGrid';
import QuestionEditForm from './QuestionEditForm';
import YearPicker from './ui/YearPicker';
import { isModelTestLevel, blueprintFor } from '../utils/modelTestBlueprint';

const Input = ({ type, placeholder, value, onChange, className, id, min, max, required, 'aria-label': ariaLabel, 'aria-describedby': ariaDescribedBy, disabled, defaultValue }) => (
    <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        className={`form-control ${className || ''}`}
        id={id}
        min={min}
        max={max}
        required={required}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        disabled={disabled}
        defaultValue={defaultValue}
    />
);

const getTotalQuestions = (questionsForQuiz) => {
    let total = 0;
    for (const subject in questionsForQuiz) {
        total += questionsForQuiz[subject].length;
    }
    return total;
};

// --- QuizAddForm Component ---
function QuizAddForm({ onCancel, onSave, questionsForQuiz, onQuestionsForQuizChange, onSelectedSubjectsChange, onEditQuestion }) {
    const { userState } = useUser();

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [startTime, setStartTime] = useState('');
    const [durationMinutes, setDurationMinutes] = useState(60);
    const [level, setLevel] = useState('');
    const [division, setDivision] = useState('');
    const [unit, setUnit] = useState('');
    const [createdBy, setCreatedBy] = useState(userState?.email || '');
    const [type, setType] = useState('');
    const [year, setYear] = useState('');
    const [quizSubjects, setQuizSubjects] = useState([]);
    // TOEFL model test: section module counts + per-module (bucket) durations. The bucket keys
    // ("Reading — Module 1", "Writing", …) double as `quizSubjects` so the existing grid works.
    const [moduleCount, setModuleCount] = useState({ reading: 2, listening: 2, writing: 1, speaking: 1, reading_writing: 2, math: 2 });
    const [durations, setDurations] = useState({});
    const setId = 1;
    // TOEFL and IELTS are both model-test levels — pick the matching blueprint helpers by level.
    const isModelTest = isModelTestLevel(level);
    const bp = blueprintFor(level);
    // BCS published/competition/micro/previous-year auto-allocate time when duration is left blank,
    // so the field is optional for those (typed value still respected).
    const durationAuto = level === 'BCS' && ['published', 'compitition', 'micro', 'previous_year'].includes(type);

    const CallApi = useApiCaller();
    const [tags, setTags] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    const fetchTags = async () => {
        try {
            const tagUrl = getTagUrl();
            const tagGetResponse = await CallApi({
                url: tagUrl,
                method: 'GET',
                token: true,
            });
            if (tagGetResponse.success) {
                setTags(tagGetResponse.data.tags);
            } else {
                setTags([]);
            }
        } catch (error) {
            console.error("Error fetching tags:", error);
            setTags([]);
        }
    };

    useEffect(() => {
        fetchTags();
    }, []);

    useEffect(() => {
        onSelectedSubjectsChange(quizSubjects);
    }, [quizSubjects]);

    // TOEFL: derive the module buckets from the section/module config; keep quizSubjects,
    // question buckets and duration defaults in sync as the module counts change.
    useEffect(() => {
        if (!isModelTest) return;
        const buckets = bp.bucketsFor(moduleCount);
        const keys = buckets.map(b => b.key);
        setQuizSubjects(keys);
        onQuestionsForQuizChange(prev => {
            const next = {};
            keys.forEach(k => { next[k] = prev[k] || []; });
            return next;
        });
        setDurations(prev => {
            const d = { ...prev };
            buckets.forEach(b => {
                if (d[b.key] == null) d[b.key] = (bp.SECTIONS.find(s => s.key === b.sectionKey) || {}).defaultMinutes;
            });
            return d;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isModelTest, level, moduleCount]);


    const handleSave = async () => {
        setError('');
        if (!title || (type && type === 'compitition' && !startTime) || (!durationAuto && !durationMinutes) || !level || !createdBy || !type || quizSubjects.length === 0 || getTotalQuestions(questionsForQuiz) === 0) {
            setError('Please fill all required fields and assign at least one question to a subject.');
            return;
        }
        if (type === 'previous_year' && !year) {
            setError('A previous-year test requires an Exam Year.');
            return;
        }
        if (type === 'micro') {
            if (quizSubjects.length !== 1) {
                setError('A micro test must use exactly one subject.');
                return;
            }
            if (getTotalQuestions(questionsForQuiz) > MICRO_MAX_QUESTIONS) {
                setError(`A micro test may contain at most ${MICRO_MAX_QUESTIONS} questions.`);
                return;
            }
        }
        setIsSaving(true);
        try {
            let newQuizPayload;
            if (isModelTest) {
                // Build the model-test blueprint; question_ids bucket by section, timers per module.
                const { blueprint, questionIdsBySection, totalMinutes } = bp.buildBlueprint({ moduleCount, durations, questionsByBucket: questionsForQuiz });
                if (Object.keys(questionIdsBySection).length === 0) {
                    setError('Assign at least one question to a module.');
                    setIsSaving(false);
                    return;
                }
                newQuizPayload = constructQuizPostPayload(
                    title, description, startTime, totalMinutes || durationMinutes, level, createdBy, type,
                    questionIdsBySection, setId, year, { division, unit, metadata: blueprint }
                );
            } else {
                const payloadQuestionIds = {};
                for (const subject in questionsForQuiz) {
                    if (questionsForQuiz[subject] && questionsForQuiz[subject].length > 0) {
                        payloadQuestionIds[subject] = questionsForQuiz[subject].map(q => q.question_id);
                    }
                }
                newQuizPayload = constructQuizPostPayload(
                    title, description, startTime, durationMinutes, level, createdBy, type,
                    payloadQuestionIds, setId, year, { division, unit }
                );
            }

            const newQuizPostUrl = getAdminQuizDeleteAndPostUrl();
            const postQuizResponse = await CallApi({
                url: newQuizPostUrl,
                method: 'POST',
                payload: newQuizPayload,
                token: true,
            });

            if (postQuizResponse.success) {
                onSave(postQuizResponse.data);
            } else {
                setError(postQuizResponse.message || 'Failed to add test.');
                console.error('Add Quiz Error:', postQuizResponse);
            }
        } catch (error) {
            setError('An error occurred while adding the test.');
            console.error('Add Quiz Error in catch block:', error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleQuizSubjectSelect = (e) => {
        const selectedSubject = e.target.value;
        // "All Subjects": make every subject at this level eligible in one click.
        if (selectedSubject === ALL_SUBJECTS_OPTION) {
            if (type === 'micro') {
                setError('A micro test must use exactly one subject; "All Subjects" is not allowed.');
                return;
            }
            const all = [...new Set(
                tags.filter(t => t.tag_type === 'SUBJECT' && t.level === level && t.is_current !== false)
                    .map(t => t.name)
            )];
            setQuizSubjects(all);
            onQuestionsForQuizChange(prev => {
                const next = { ...prev };
                all.forEach(s => { next[s] = next[s] || []; });
                return next;
            });
            return;
        }
        if (type === 'micro' && quizSubjects.length >= 1 && !quizSubjects.includes(selectedSubject)) {
            setError('A micro test must use exactly one subject. Remove the current subject to pick another.');
            return;
        }
        if (selectedSubject && !quizSubjects.includes(selectedSubject)) {
            setQuizSubjects(prev => [...prev, selectedSubject]);
            onQuestionsForQuizChange(prev => ({
                ...prev,
                [selectedSubject]: prev[selectedSubject] || []
            }));
        }
    };

    const handleRemoveQuizSubject = (subjectToRemove) => {
        setQuizSubjects(prev => prev.filter(sub => sub !== subjectToRemove));
        onQuestionsForQuizChange(prev => {
            const newQuestions = { ...prev };
            delete newQuestions[subjectToRemove];
            return newQuestions;
        });
    };

    // Block reducing a section's module count while a to-be-removed module still holds questions
    // (otherwise those assignments would silently vanish).
    const guardedSetModuleCount = (sec, newCount) => {
        const cur = moduleCount[sec.key] || 1;
        if (newCount < cur) {
            for (let i = newCount; i < cur; i += 1) {
                const b = bp.bucketKey(sec.name, i);
                if ((questionsForQuiz[b] || []).length > 0) {
                    setError(`Remove all questions from ${sec.name} Module ${i + 1} before reducing to ${newCount} module.`);
                    return;
                }
            }
        }
        setError('');
        setModuleCount(c => ({ ...c, [sec.key]: newCount }));
    };

    const isSaveDisabled = isSaving || !title || (type && type === 'compitition' && !startTime) || (type === 'previous_year' && !year) || (!durationAuto && !durationMinutes) || !level || !createdBy || !type || quizSubjects.length === 0 || getTotalQuestions(questionsForQuiz) === 0;

    return (
        <div className="card shadow-sm p-4 h-full" style={{ borderTop: '4px solid #7c3aed' }}>
            <h2 className="text-2xl font-semibold mb-3" style={{ color: '#5b21b6' }}>Add New Test</h2>
            {error && <div className="alert alert-danger">{error}</div>}
            <div className="space-y-4">
                <div className="my-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Test Title <span className="text-danger">*</span>
                    </label>
                    <textarea
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="form-control"
                        rows={1}
                        placeholder="Enter test title"
                        required
                    />
                </div>
                <div className="my-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Test Description
                    </label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="form-control"
                        rows={2}
                        placeholder="Enter test description"
                    />
                </div>
                <div className="my-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Test Level <span className="text-danger">*</span></label>
                    <select
                        value={level}
                        onChange={(e) => {
                            setLevel(e.target.value);
                            setQuizSubjects([]);
                            setDivision('');
                            setUnit('');
                            onQuestionsForQuizChange({});
                        }}
                        className="form-select"
                        required
                    >
                        <option value="" disabled>SELECT</option>
                        {TAGLEVELS.map((item) => (
                            item.submenu ? (
                                <optgroup key={item.value} label={item.label}>
                                    {item.submenu.map((sub) => (
                                        <option key={sub.value} value={sub.value}>
                                            {sub.label}
                                        </option>
                                    ))}
                                </optgroup>
                            ) : (
                                <option key={item.value} value={item.value}>
                                    {item.label}
                                </option>
                            )
                        ))}
                    </select>
                </div>
                {isDivisionLevel(level) && (
                    <div className="my-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Division</label>
                        <select value={division} onChange={(e) => setDivision(e.target.value)} className="form-select">
                            <option value="">All / Mixed</option>
                            {DIVISION_VALUES.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                    </div>
                )}
                {isUnitLevel(level) && (
                    <div className="my-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
                        <select value={unit} onChange={(e) => setUnit(e.target.value)} className="form-select">
                            <option value="">All / Mixed</option>
                            {UNIT_VALUES.map(u => <option key={u} value={u}>{u}</option>)}
                        </select>
                    </div>
                )}
                <div className="my-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Test Type <span className="text-danger">*</span></label>
                    <select
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        className="form-select"
                        required
                    >
                        <option value="" disabled>SELECT</option>
                        {QUIZTYPE.filter((qType) => qType !== 'daily').map((qType) => (
                            <option key={qType} value={qType}>
                                {QUIZ_TYPE_LABELS[qType] || qType}
                            </option>
                        ))}
                    </select>
                    {type === 'micro' && (
                        <small className="form-text text-muted">
                            Micro test: exactly one subject, up to {MICRO_MAX_QUESTIONS} questions. Created as a draft — publish it from Manage Tests.
                        </small>
                    )}
                </div>
                {type === 'previous_year' &&
                    <div className="my-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Exam Year <span className="text-danger">*</span>
                        </label>
                        <YearPicker
                            value={year}
                            onChange={setYear}
                            placeholder="SELECT"
                        />
                        <small className="form-text text-muted">The exam year this test's questions are from.</small>
                    </div>
                }
                {type && type === 'compitition' &&
                    <div className="my-3">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Start Date & Time <span className="text-danger">*</span>
                        </label>
                        <Input
                            type="datetime-local"
                            value={startTime}
                            onChange={(e) => setStartTime(e.target.value)}
                            className="form-control"
                            required
                        />
                    </div>
                }
                
                {!isModelTest && (
                <div className="my-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Duration In Minutes {durationAuto ? <span className="text-muted small">(optional — leave blank to auto-allocate)</span> : <span className="text-danger">*</span>}</label>
                    <Input
                        type="number"
                        placeholder={durationAuto ? 'Auto-allocated if left blank' : 'Duration'}
                        value={durationMinutes}
                        onChange={(e) => setDurationMinutes(e.target.value === '' ? '' : Number(e.target.value))}
                        className="w-full"
                        required={!durationAuto}
                    />
                </div>
                )}
                
                <div className="my-3">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Created By <span className="text-danger">*</span></label>
                    <Input
                        type="email"
                        placeholder="Creator Email"
                        value={createdBy}
                        onChange={(e) => setCreatedBy(e.target.value)}
                        className="w-full"
                        required
                    />
                </div>

                {/* Subject / section selection */}
                {level && level.length > 0 && isModelTest && (
                    <div className="w-full">
                        <label className="block text-sm font-medium text-gray-700 mb-1">{level} Sections &amp; Modules</label>
                        <small className="form-text text-muted d-block mb-2">Set modules per section and each module's time. Assign questions to a section-module from the Available Questions grid.</small>
                        {bp.SECTIONS.map(sec => {
                            const count = sec.maxModules === 1 ? 1 : (moduleCount[sec.key] || 1);
                            return (
                                <div key={sec.key} className="border rounded p-2 mb-2">
                                    <div className="d-flex justify-content-between align-items-center">
                                        <strong>{sec.name}</strong>
                                        {sec.maxModules > 1 && (
                                            <select className="form-select form-select-sm w-auto" value={moduleCount[sec.key] || 1}
                                                onChange={e => guardedSetModuleCount(sec, Number(e.target.value))}>
                                                {Array.from({ length: sec.maxModules }).map((_, n) => (
                                                    <option key={n + 1} value={n + 1}>{n + 1} module{n > 0 ? 's' : ''}</option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                    <div className="d-flex flex-wrap gap-3 mt-2">
                                        {Array.from({ length: count }).map((_, i) => {
                                            const bucket = bp.bucketKey(sec.name, i);
                                            return (
                                                <div key={i} className="d-flex align-items-center gap-1">
                                                    <span className="small text-muted">{count > 1 ? `Module ${i + 1}` : 'Time'}:</span>
                                                    <input type="number" min="1" style={{ width: 70 }} className="form-control form-control-sm"
                                                        value={durations[bucket] ?? sec.defaultMinutes}
                                                        onChange={e => setDurations(d => ({ ...d, [bucket]: e.target.value }))} />
                                                    <span className="small text-muted">min · {(questionsForQuiz[bucket] || []).length} Q</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
                {level && level.length > 0 && !isModelTest &&
                    <div className="w-full">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Test Subjects <span className="text-danger">*</span></label>
                        <div className="d-flex flex-wrap gap-2 mb-2">
                            {quizSubjects.map((sub) => (
                                <span
                                    key={sub}
                                    className="badge bg-primary-subtle text-primary d-flex align-items-center rounded-pill px-2 py-1"
                                >
                                    {sub}
                                    <XCircle className="text-danger cursor-pointer ms-1" size={16} onClick={() => handleRemoveQuizSubject(sub)}/>
                                </span>
                            ))}
                        </div>
                        <select
                            value=""
                            onChange={handleQuizSubjectSelect}
                            className="form-select"
                            disabled={!level}
                        >
                            <option value="" disabled>Add Subject to Test</option>
                            {type !== 'micro' && (
                                <option value={ALL_SUBJECTS_OPTION}>All Subjects</option>
                            )}
                            {tags
                                .filter((tag) => tag.tag_type === 'SUBJECT' && tag.level === level && tag.is_current !== false)
                                .filter((tag) => !quizSubjects.includes(tag.name))
                                .map((tag) => (
                                    <option key={tag.name} value={tag.name}>
                                        {tag.name}
                                    </option>
                                ))}
                        </select>
                        {!level && <small className="form-text text-muted">Select a Level to add Subjects.</small>}
                    </div>
                }
                <div className="text-end my-3">
                    <p className="fw-bold">Total Questions Added: {getTotalQuestions(questionsForQuiz)}</p>
                </div>

                {/* Subject-specific Question Grids */}
                {quizSubjects.map(subjectName => (
                    <SubjectQuestionGrid
                        key={subjectName}
                        subjectName={subjectName}
                        assignedQuestions={questionsForQuiz[subjectName] || []}
                        onRemoveQuestion={(questionId) => onQuestionsForQuizChange(prev => {
                            const newQuestions = { ...prev };
                            if (newQuestions[subjectName]) {
                                newQuestions[subjectName] = newQuestions[subjectName].filter(q => q.question_id !== questionId);
                            }
                            return newQuestions;
                        })}
                        onEditQuestion={onEditQuestion}
                    />
                ))}

                <div className="d-flex justify-content-end gap-2 mt-4">
                    <button className='btn btn-danger' onClick={onCancel} disabled={isSaving}>
                        Cancel
                    </button>
                    <button className='btn btn-primary' onClick={handleSave} disabled={isSaveDisabled}>
                        {isSaving ? 'Creating...' : 'Create The Test'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function QuizAddManagementPage({onCancel, onSave}) {
    const [currentPage, setCurrentPage] = useState(1);
    const [questionFilters, setQuestionFilters] = useState({
        level: "", difficulty: "", search: "", topic: "", subject: "", exam: "", random: false,
    });
    const [questionsForQuiz, setQuestionsForQuiz] = useState({});
    const [selectedQuizSubjects, setSelectedQuizSubjects] = useState([]);
    const CallApi = useApiCaller();
    // Click-to-edit: full question (fetched by id) for the QuestionEditForm modal.
    const [editingQuestion, setEditingQuestion] = useState(null);

    const handleEditQuestion = useCallback(async (question) => {
        const id = question?.question_id;
        if (!id) return;
        const res = await CallApi({ url: getAdminQuestionGetByIdUrl(id), method: 'GET', token: true });
        if (res.success) setEditingQuestion(res.data);
    }, [CallApi]);

    const handleFilterQuestions = useCallback((newFilters) => {
        setQuestionFilters(newFilters);
    }, []);

    const handleQuestionAssign = useCallback((question, subjectName) => {
        setQuestionsForQuiz(prev => {
            const currentQuestionsInSubject = prev[subjectName] || [];
            if (!currentQuestionsInSubject.some(q => q.question_id === question.question_id)) {
                return {
                    ...prev,
                    [subjectName]: [...currentQuestionsInSubject, question]
                };
            }
            return prev;
        });
    }, []);

    const handleQuestionsForQuizChange = useCallback((updater) => {
        if (typeof updater === 'function') {
            setQuestionsForQuiz(updater);
        } else {
            setQuestionsForQuiz(updater);
        }
    }, []);

    const handleSelectedQuizSubjectsChange = useCallback((subjects) => {
        setSelectedQuizSubjects(subjects);
    }, []);

    return (
        <div className="container-fluid py-4">
            <h1 className="fw-bold mb-4" style={{ fontSize: '1.6rem', color: '#5b21b6' }}>Create New Test</h1>

            <div className="row">
                <div className="col-lg-6 mb-4">
                    <QuizAddForm
                        onCancel={onCancel}
                        onSave={onSave}
                        questionsForQuiz={questionsForQuiz}
                        onQuestionsForQuizChange={setQuestionsForQuiz}
                        onSelectedSubjectsChange={handleSelectedQuizSubjectsChange}
                        onEditQuestion={handleEditQuestion}
                    />
                </div>

                <div className="col-lg-6 mb-4">
                    <div className="card shadow-sm p-4 h-full">
                        <QuestionFilterBar onFilter={handleFilterQuestions} />
                        <QuestionSelectionGrid
                            filters={questionFilters}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                            onQuestionAssign={handleQuestionAssign}
                            selectedQuizSubjects={selectedQuizSubjects}
                            onEditQuestion={handleEditQuestion}
                        />
                    </div>
                </div>
            </div>
            <style>
                {`
                input[type="datetime-local"].form-control {
                    padding: 0.375rem 0.75rem;
                    border: 1px solid #ced4da;
                    border-radius: 0.25rem;
                    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.075);
                    font-size: 1rem;
                    line-height: 1.5;
                    height: calc(1.5em + 0.75rem + 2px);
                }
                input[type="datetime-local"].form-control:focus {
                    color: #212529;
                    background-color: #fff;
                    border-color: #86b7fe;
                    outline: 0;
                    box-shadow: 0 0 0 0.25rem rgba(13,110,253,.25);
                }

                .badge.bg-primary-subtle {
                    background-color: #cce5ff !important;
                    color: #004085 !important;
                }
                `}
            </style>
            {editingQuestion && (
                <QuestionEditForm
                    question={editingQuestion}
                    onCancel={() => setEditingQuestion(null)}
                    onSave={() => setEditingQuestion(null)}
                />
            )}
        </div>
    );
}
